import type { ListerSessionSummary } from "../../../lib/api";
import { hashFor } from "../../../lib/routes";

// A session is unfinished exactly when it was researched into a proposal but never posted
// ("proposed"), or scanned and never carried any further ("scanned") — the owner's own words.
// Every other status is either mid-flight (actively being worked by the pipeline right now,
// not sitting neglected) or already finished (posted/restocked/enriched) or dead (failed, which
// needs a retry, not an "add to inventory" nudge). Exported so this definition is one place and
// the banner's test can drive it directly instead of re-deriving it.
export function isPendingSession(s: Pick<ListerSessionSummary, "status">): boolean {
  return s.status === "scanned" || s.status === "proposed";
}

export default function PendingBanner({ pending, onDismiss }: { pending: ListerSessionSummary[]; onDismiss: () => void }) {
  if (pending.length === 0) return null;
  // Sessions arrive newest-first (see GET /api/lister/sessions); the last one still pending in
  // that window is the oldest of the bunch, and "the one that has waited longest" is the most
  // useful single link to hand over when the ask is "give them a way straight to one."
  const oldest = pending[pending.length - 1]!;
  return (
    <div className="border border-amber-700/60 bg-amber-900/20 text-amber-200 rounded-md p-3 space-y-2" role="status">
      <p>
        {pending.length === 1
          ? "There is a pending scan — researched or scanned, but never added to inventory."
          : `There are ${pending.length} pending scans — researched or scanned, but never added to inventory.`}
      </p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <a href={hashFor.review(oldest.id)} className="underline text-amber-100">
          Add it to inventory now
        </a>
        <button type="button" onClick={onDismiss} className="ml-auto underline text-amber-300/70 hover:text-amber-200">
          not now
        </button>
      </div>
    </div>
  );
}
